/**
 * The campaign request form for brands (specification §4.3).
 *
 * The brand proposes the terms and the brief in three numbered sections. A summary beside the
 * form shows what the numbers mean as they are typed: the split of the budget, the cost per
 * thousand units, the creator threshold, the window, and what happens after sending. The brief
 * can carry a brand kit: images such as the logo, uploaded as they are added, which creators may
 * use and the visual checks compare content with. The admin team reviews the request and accepts
 * it, which gives the deposit details, or rejects it with a note. Problems are checked with the
 * API's own rules before sending.
 *
 * `/campaigns/new?from=<id>` starts from one of the brand's earlier requests, to send it again
 * after a rejection (see `formFromRequest`).
 *
 * The same form changes a request, which only its brand does (D25): `/campaigns/requests/:id/edit`
 * edits a request waiting for review, and `/campaigns/requests/:id/change` asks for a change of an
 * accepted or lapsed one, with the reason, for the admin team to review. Both start from the
 * request's terms, its dates included.
 */

import {
  BRAND_ASSET_TYPES,
  type CampaignRequestInput,
  campaignDayMs,
  formatCampaignDays,
  formatTokenAmount,
  hasShortDays,
  MAX_BRAND_ASSET_BYTES,
  MAX_BRAND_ASSETS,
  MAX_X_CAMPAIGN_DAYS,
  MIN_ACCEPTANCE_LEAD_TIME_DAYS,
  MIN_LEAD_TIME_DAYS,
  parseTokenAmount,
  primaryMetric,
  threshold,
} from "@morcast/campaigns";
import { ImagePlus, X as Remove } from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { privateAssetUrl } from "../../campaigns/BriefCard.js";
import {
  type BrandKitImage,
  calendarBounds,
  emptyForm,
  formFromRequest,
  type RequestForm,
  toRequest,
} from "../../campaigns/form.js";
import {
  type CampaignRequest,
  pendingChange,
  useAcceptedTokens,
  useAskChange,
  useEditRequest,
  useRequest,
  useSubmitRequest,
  useUploadBrandImage,
  useUploadingBrandImage,
} from "../../campaigns/requests.js";
import { DatePicker, DateTimeField, Radio, Select } from "../../components/forms.js";
import { RequireWallet } from "../../components/RequireWallet.js";
import {
  ErrorMessage,
  Loading,
  Notice,
  PageHeader,
  Problems,
  styles,
} from "../../components/ui.js";

export function NewRequest() {
  return (
    <RequireWallet reason="Campaign requests belong to the wallet that will make the deposit.">
      <NewRequestPage />
    </RequireWallet>
  );
}

/** The form for a change of the brand's request, waiting for review or accepted (D25). */
export function ChangeRequest({ mode }: { mode: "edit" | "change" }) {
  return (
    <RequireWallet reason="Sign in with the wallet you used for this request.">
      <ChangeRequestPage mode={mode} />
    </RequireWallet>
  );
}

function NewRequestPage() {
  const tokens = useAcceptedTokens();
  // The earlier request to send again, if any. The form waits for it.
  const from = Number(useSearchParams()[0].get("from")) || 0;
  const source = useRequest(from, from > 0);
  const ready = from === 0 || !source.isPending;

  return (
    <div className={styles.page}>
      <PageHeader eyebrow="For brands" title="Start a campaign">
        Tell us about your campaign. The Morcast team reviews every request and accepts it or
        rejects it with a note. Once it is accepted, you deposit the budget. You only pay for
        verified organic reach, and you withdraw what is not spent.
      </PageHeader>
      <ShortDaysNotice />
      {(tokens.isPending || !ready) && (
        <div className="mt-8">
          <Loading />
        </div>
      )}
      {tokens.isError && <ErrorMessage error={tokens.error} />}
      {tokens.data?.length === 0 && <NotTakingCampaigns />}
      {tokens.data && tokens.data.length > 0 && ready && (
        <>
          {source.isError && (
            <Notice tone="warning" className="mt-8">
              Request {from} could not be opened, so the form starts empty.
            </Notice>
          )}
          <RequestFormView mode="new" tokens={tokens.data} source={source.data} />
        </>
      )}
    </div>
  );
}

/** On a test network set up with short campaign days, how long they last and what that changes. */
function ShortDaysNotice() {
  if (!hasShortDays()) {
    return null;
  }
  return (
    <Notice tone="info" className="mt-8">
      This test network runs on short days. Each campaign day lasts {formatCampaignDays(1)}, so a
      whole campaign, from this request to the settlement, takes about{" "}
      {Math.round((15 * campaignDayMs()) / 60_000)} minutes. YouTube campaigns need real days, so
      run an X campaign here.
    </Notice>
  );
}

function NotTakingCampaigns() {
  return (
    <Notice tone="info" className="mt-8">
      Morcast is not taking new campaigns right now. Please come back later.
    </Notice>
  );
}

/** Why a request cannot take this kind of change, or null when it can. */
function notChangeable(request: CampaignRequest, mode: "edit" | "change"): string | null {
  if (mode === "edit") {
    return request.status === "submitted"
      ? null
      : request.status === "accepted" || request.status === "lapsed"
        ? "The accepted terms are fixed. Ask for a change instead."
        : "Only a request waiting for review can be edited.";
  }
  if (request.status === "submitted") {
    return "The request is waiting for review. Edit it instead.";
  }
  if (request.status !== "accepted" && request.status !== "lapsed") {
    return request.status === "funded"
      ? "The deposit fixed the terms on-chain, so they can no longer change."
      : "The request has ended. Send it again with the new terms.";
  }
  return pendingChange(request)
    ? "A change of this request is already waiting for review. Withdraw it to ask for another."
    : null;
}

function ChangeRequestPage({ mode }: { mode: "edit" | "change" }) {
  const id = Number(useParams().id);
  const tokens = useAcceptedTokens();
  const request = useRequest(id);
  const refusal = request.data ? notChangeable(request.data, mode) : null;

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow={`Campaign request ${id}`}
        title={mode === "edit" ? "Edit the request" : "Change the request"}
        documentTitle={mode === "edit" ? "Edit the request" : "Change the request"}
      >
        {mode === "edit"
          ? "The request is still waiting for review, so your edit replaces its terms. The Morcast team reviews the request as you leave it."
          : request.data?.status === "lapsed"
            ? "No deposit arrived before the start, so the request lapsed. Ask for new dates and anything else you need. The Morcast team reviews the change, and once it is accepted you deposit again before the new start."
            : "Your request is accepted, so its terms are fixed. Ask for the change you need. The Morcast team reviews it, and until it is decided the accepted terms stay in force."}
      </PageHeader>
      <Link to={`/campaigns/requests/${id}`} className={`mt-4 inline-block text-sm ${styles.link}`}>
        Back to the request
      </Link>
      {(tokens.isPending || request.isPending) && (
        <div className="mt-8">
          <Loading />
        </div>
      )}
      {tokens.isError && <ErrorMessage error={tokens.error} />}
      {request.isError && <ErrorMessage error={request.error} />}
      {refusal && (
        <Notice tone="warning" className="mt-8">
          {refusal}
        </Notice>
      )}
      {tokens.data?.length === 0 && <NotTakingCampaigns />}
      {tokens.data && tokens.data.length > 0 && request.data && !refusal && (
        <RequestFormView mode={mode} tokens={tokens.data} source={request.data} />
      )}
    </div>
  );
}

/** The window fields of a form, to tell whether a change keeps the request's dates. */
function datesOf(form: RequestForm): string {
  return form.platform === "X"
    ? `${form.startAt}|${form.endAt}`
    : `${form.firstDate}|${form.finalDate}`;
}

/**
 * The request form, for a new request or for a change of the brand's request (D25).
 *
 * - `new`: sends a new request. `source`, if any, is an earlier request it starts from.
 * - `edit`: replaces the terms of `source`, a request waiting for review.
 * - `change`: asks for a change of `source`, an accepted or lapsed request, with a reason.
 */
function RequestFormView({
  mode,
  tokens,
  source,
}: {
  mode: "new" | "edit" | "change";
  tokens: { address: string; symbol: string; decimals: number }[];
  source: CampaignRequest | undefined;
}) {
  const navigate = useNavigate();
  const submitNew = useSubmitRequest();
  const edit = useEditRequest();
  const askChange = useAskChange();
  const submit = mode === "new" ? submitNew : mode === "edit" ? edit : askChange;
  const uploading = useUploadingBrandImage();
  const [form, setForm] = useState<RequestForm>(() =>
    source
      ? formFromRequest(source, tokens, new Date(), privateAssetUrl, { keepDates: mode !== "new" })
      : emptyForm(tokens),
  );
  // The form as it started, to tell whether a change keeps the request's dates.
  const [initial] = useState(form);
  // Whether the earlier request's dates were left out, being too soon for a new request.
  const [datesLeftOut] = useState(() => source !== undefined && !form.startAt && !form.firstDate);
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const [problems, setProblems] = useState<string[]>([]);

  const token = tokens.find((entry) => entry.address === form.token);
  const metric = primaryMetric(form.platform, form.platform === "YOUTUBE" ? form.format : null);
  const bounds = calendarBounds(form, new Date());

  /** Updates one text field with a value. */
  function setValue(field: Exclude<keyof RequestForm, "assets">) {
    return (value: string) => setForm((current) => ({ ...current, [field]: value }));
  }

  /** Updates one text field from an input or textarea. */
  function set(field: Exclude<keyof RequestForm, "assets">) {
    return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValue(field)(event.target.value);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    // The request waits for the brand kit. An image still uploading would be left out.
    if (uploading) {
      return;
    }
    // A change that keeps an accepted request's dates needs only time to deposit. New dates need
    // the lead time of a new request.
    const keepsDates =
      mode === "change" && form.platform === initial.platform && datesOf(form) === datesOf(initial);
    const result = toRequest(
      form,
      tokens,
      new Date(),
      keepsDates ? { minLeadTimeDays: MIN_ACCEPTANCE_LEAD_TIME_DAYS } : {},
    );
    const reasonProblems =
      mode === "change" && reason.trim().length < 5
        ? ["Say in a few words what changes and why."]
        : [];
    if (!result.ok || reasonProblems.length > 0) {
      setProblems([...(result.ok ? [] : result.problems), ...reasonProblems]);
      return;
    }
    setProblems([]);
    const terms: CampaignRequestInput = result.request;
    const done = (request: CampaignRequest) => navigate(`/campaigns/requests/${request.id}`);
    if (mode === "new") {
      submitNew.mutate(terms, { onSuccess: done });
    } else if (mode === "edit" && source) {
      edit.mutate({ id: source.id, terms }, { onSuccess: done });
    } else if (source) {
      askChange.mutate({ id: source.id, terms, reason: reason.trim() }, { onSuccess: done });
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]"
    >
      <div className="min-w-0 space-y-8">
        {mode === "new" && source && (
          <Notice tone="info" title={`Starting from request ${source.id}`}>
            The form holds that request's terms and brief
            {datesLeftOut ? ", but not its dates, which are too soon for a new request" : ""}.
            Change what you need and send it. Morcast reviews it as a new request.
          </Notice>
        )}
        <Section
          number={1}
          title="Brand"
          description="Who the campaign is for, and how the Morcast team can reach you."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Brand name">
              {(field) => (
                <input
                  {...field}
                  value={form.brandName}
                  onChange={set("brandName")}
                  className={styles.input}
                />
              )}
            </Field>
            <Field label="Contact email" hint="Only the Morcast team sees it.">
              {(field) => (
                <input
                  {...field}
                  type="email"
                  value={form.contactEmail}
                  onChange={set("contactEmail")}
                  className={styles.input}
                />
              )}
            </Field>
          </div>
        </Section>

        <Section
          number={2}
          title="Campaign"
          description="The platform, what the budget pays for, and when the campaign runs."
        >
          <fieldset>
            <legend className="text-sm font-medium">Platform</legend>
            <div className="mt-2 flex gap-3">
              {(["X", "YOUTUBE"] as const).map((platform) => (
                <Choice
                  key={platform}
                  name="platform"
                  checked={form.platform === platform}
                  onChange={() => setForm((current) => ({ ...current, platform }))}
                  label={platform === "X" ? "X" : "YouTube"}
                />
              ))}
            </div>
          </fieldset>

          {form.platform === "YOUTUBE" && (
            <fieldset className="mt-4">
              <legend className="text-sm font-medium">Format</legend>
              <div className="mt-2 flex flex-wrap gap-3">
                <Choice
                  name="format"
                  checked={form.format === "DEDICATED"}
                  onChange={() => setForm((current) => ({ ...current, format: "DEDICATED" }))}
                  label="Dedicated video"
                  hint="The whole video is about your brand."
                />
                <Choice
                  name="format"
                  checked={form.format === "INTEGRATION"}
                  onChange={() => setForm((current) => ({ ...current, format: "INTEGRATION" }))}
                  label="Integration"
                  hint="A branded segment within a video."
                />
              </div>
            </fieldset>
          )}

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="Token">
              {(field, labelId) => (
                <Select
                  {...field}
                  labelId={labelId}
                  value={form.token}
                  options={tokens.map((entry) => ({ value: entry.address, label: entry.symbol }))}
                  onChange={setValue("token")}
                />
              )}
            </Field>
            <Field
              label={`Budget (${token?.symbol ?? "tokens"})`}
              hint="The most you pay. What is not earned is yours to withdraw."
            >
              {(field) => (
                <input
                  {...field}
                  inputMode="decimal"
                  value={form.budget}
                  onChange={set("budget")}
                  placeholder="100,000"
                  className={styles.input}
                />
              )}
            </Field>
            <Field label="Target" hint={`${metric.unit} that earn the whole budget`}>
              {(field) => (
                <input
                  {...field}
                  inputMode="numeric"
                  value={form.target}
                  onChange={set("target")}
                  placeholder="1,000,000"
                  className={styles.input}
                />
              )}
            </Field>
          </div>

          {form.platform === "X" ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field
                label="Start (UTC)"
                hint={`At least ${formatCampaignDays(MIN_LEAD_TIME_DAYS)} from now, for the review and the deposit.`}
              >
                {(field, labelId) => (
                  <DateTimeField
                    {...field}
                    labelId={labelId}
                    timeLabel="Start time (UTC)"
                    value={form.startAt}
                    onChange={setValue("startAt")}
                    min={bounds.firstStart}
                  />
                )}
              </Field>
              <Field
                label="End (UTC)"
                hint={`An X campaign lasts at most ${formatCampaignDays(MAX_X_CAMPAIGN_DAYS)}.`}
              >
                {(field, labelId) => (
                  <DateTimeField
                    {...field}
                    labelId={labelId}
                    timeLabel="End time (UTC)"
                    value={form.endAt}
                    onChange={setValue("endAt")}
                    min={bounds.firstEnd}
                    max={bounds.lastEnd}
                  />
                )}
              </Field>
            </div>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="First reporting date">
                {(field, labelId) => (
                  <DatePicker
                    {...field}
                    labelId={labelId}
                    value={form.firstDate}
                    onChange={setValue("firstDate")}
                    min={bounds.firstStart}
                  />
                )}
              </Field>
              <Field
                label="Final reporting date"
                hint="YouTube's reporting dates, in Los Angeles time."
              >
                {(field, labelId) => (
                  <DatePicker
                    {...field}
                    labelId={labelId}
                    value={form.finalDate}
                    onChange={setValue("finalDate")}
                    min={bounds.firstEnd}
                    max={bounds.lastEnd}
                  />
                )}
              </Field>
            </div>
          )}

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field
              label="Submissions per account"
              hint="Posts or videos each of a creator's accounts may submit. A creator takes part with up to 5 accounts."
            >
              {(field, labelId) => (
                <Select
                  {...field}
                  labelId={labelId}
                  value={form.submissionLimit}
                  options={["1", "2", "3"].map((limit) => ({ value: limit, label: limit }))}
                  onChange={setValue("submissionLimit")}
                />
              )}
            </Field>
          </div>

          <p className="mt-6 rounded-xl bg-zinc-50 p-4 text-sm text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
            {metric.description}
          </p>
        </Section>

        <Section
          number={3}
          title="Brief"
          description="What creators publish. Morcast checks every item against each requirement."
        >
          <div className="space-y-4">
            <Field label="Title">
              {(field) => (
                <input
                  {...field}
                  value={form.title}
                  onChange={set("title")}
                  className={styles.input}
                />
              )}
            </Field>
            <Field label="Description" hint="What the campaign promotes and the key messages.">
              {(field) => (
                <textarea
                  {...field}
                  rows={5}
                  value={form.description}
                  onChange={set("description")}
                  className={styles.input}
                />
              )}
            </Field>
            <Field label="Disclosure" hint="The paid-promotion label creators must show.">
              {(field) => (
                <input
                  {...field}
                  value={form.disclosure}
                  onChange={set("disclosure")}
                  className={styles.input}
                />
              )}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <ListField
                label="Hashtags"
                value={form.requiredHashtags}
                onChange={set("requiredHashtags")}
                placeholder="#acme"
              />
              <ListField
                label="Mentions"
                value={form.requiredMentions}
                onChange={set("requiredMentions")}
                placeholder="@acme"
              />
            </div>
            <ListField
              label="Links"
              value={form.requiredLinks}
              onChange={set("requiredLinks")}
              placeholder="https://acme.example/launch"
            />
            <ListField
              label="Required phrases"
              value={form.requiredText}
              onChange={set("requiredText")}
            />
            <ListField
              label="Content requirements"
              hint="Each line is checked on its own, so write one requirement per line."
              value={form.semanticRequirements}
              onChange={set("semanticRequirements")}
            />
            <ListField
              label="Visual requirements"
              hint={`Each line is checked on its own against the post's images or the video's frames. Name a brand kit image to compare content with it, as in "The Logo is visible."`}
              value={form.visualRequirements}
              onChange={set("visualRequirements")}
            />
            <BrandKitField
              images={form.assets}
              onChange={(update) =>
                setForm((current) => ({ ...current, assets: update(current.assets) }))
              }
            />
            <ListField
              label="Prohibited content"
              value={form.prohibitedContent}
              onChange={set("prohibitedContent")}
            />
            <Field label="Allowed edits after submission">
              {(field) => (
                <textarea
                  {...field}
                  rows={2}
                  value={form.allowedEdits}
                  onChange={set("allowedEdits")}
                  className={styles.input}
                />
              )}
            </Field>
          </div>
        </Section>

        {mode === "change" && (
          <div className={styles.card}>
            <label htmlFor={reasonId} className="block text-sm font-medium">
              What changes, and why
            </label>
            <textarea
              id={reasonId}
              rows={3}
              maxLength={1_000}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className={styles.input}
            />
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              The Morcast team reads this with the change, and you see their decision here.
            </p>
          </div>
        )}

        {problems.length > 0 && (
          <Problems message="Please fix these before sending:" problems={problems} />
        )}
        {submit.isError && <ErrorMessage error={submit.error} />}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="submit"
            disabled={submit.isPending || uploading}
            className={styles.primaryButton}
          >
            {submit.isPending
              ? "Sending…"
              : uploading
                ? "Uploading the image…"
                : mode === "new"
                  ? "Send request"
                  : mode === "edit"
                    ? "Save the request"
                    : "Ask for the change"}
          </button>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            {mode === "new"
              ? "Sending a request is free. You only deposit after Morcast accepts it. The request belongs to the wallet you are signed in with, which deposits and gets back any unspent budget."
              : mode === "edit"
                ? "The request stays waiting for review, with these terms."
                : `New dates must start at least ${formatCampaignDays(MIN_LEAD_TIME_DAYS)} from now, to leave time for the review and the deposit.`}
          </p>
        </div>
      </div>

      <aside aria-label="Summary" className="lg:sticky lg:top-20">
        <Summary form={form} token={token} unit={metric.unit} change={mode === "change"} />
      </aside>
    </form>
  );
}

/** What the budget, target and dates mean, updated as the brand types. */
function Summary({
  form,
  token,
  unit,
  change,
}: {
  form: RequestForm;
  token: { symbol: string; decimals: number } | undefined;
  unit: string;
  /** Whether the form asks for a change of an accepted request, not for a new one. */
  change: boolean;
}) {
  const target = /^\d[\d,]*$/.test(form.target.trim())
    ? BigInt(form.target.replaceAll(",", ""))
    : 0n;
  let budget = 0n;
  try {
    budget = token ? parseTokenAmount(form.budget, token.decimals) : 0n;
  } catch {
    budget = 0n;
  }
  const amount = (units: bigint) =>
    token ? `${formatTokenAmount(units, token.decimals)} ${token.symbol}` : "";
  const days = windowDays(form);

  return (
    <div className={`text-sm ${styles.card}`}>
      <h2 className="text-base font-semibold">Your campaign</h2>
      <dl className="mt-4 space-y-3">
        <SummaryLine label="Platform">
          {form.platform === "X"
            ? "X"
            : form.format === "INTEGRATION"
              ? "YouTube, integration"
              : "YouTube, dedicated video"}
        </SummaryLine>
        <SummaryLine label="Budget">{budget > 0n ? amount(budget) : "Not set"}</SummaryLine>
        {budget > 0n && (
          <SummaryLine label="Split at the target">
            {amount((budget * 4n) / 5n)} to creators, {amount(budget / 5n)} to Morcast
          </SummaryLine>
        )}
        <SummaryLine label="Target">
          {target > 0n ? `${target.toLocaleString("en-US")} ${unit}` : "Not set"}
        </SummaryLine>
        {target > 0n && budget > 0n && token && (
          <SummaryLine label={`Cost per 1,000 ${unit}`}>
            {formatPerThousand(budget, target, token.decimals)} {token.symbol}
          </SummaryLine>
        )}
        {target > 0n && (
          <SummaryLine label="Creator threshold">
            Each creator needs at least {threshold(target).toLocaleString("en-US")} {unit} to earn.
          </SummaryLine>
        )}
        <SummaryLine label="Window">
          {days === null ? "Not set" : formatCampaignDays(days)}
        </SummaryLine>
      </dl>
      <h3 className="mt-6 text-sm font-semibold">After you send it</h3>
      <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-zinc-600 dark:text-zinc-400">
        {change ? (
          <>
            <li>
              The Morcast team reviews your change, then accepts it or rejects it with a note.
            </li>
            <li>Once it is accepted, you deposit with the new terms before the start.</li>
          </>
        ) : (
          <>
            <li>
              The Morcast team reviews your request, then accepts it or rejects it with a note.
            </li>
            <li>Once it is accepted, you deposit the budget into the escrow before the start.</li>
          </>
        )}
        <li>Creators publish during the window, and Morcast checks every item.</li>
        <li>
          After the end, you pay only for recognized delivery and withdraw the rest of the budget
          from the escrow.
        </li>
      </ol>
    </div>
  );
}

function SummaryLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-0.5 font-medium">{children}</dd>
    </div>
  );
}

/** The campaign's length in whole campaign days, or null until both ends are set in order. */
function windowDays(form: RequestForm): number | null {
  const [start, end] =
    form.platform === "X"
      ? [Date.parse(`${form.startAt}Z`), Date.parse(`${form.endAt}Z`)]
      : [
          Date.parse(`${form.firstDate}T00:00Z`),
          Date.parse(`${form.finalDate}T00:00Z`) + 86_400_000,
        ];
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return null;
  }
  return Math.round((end - start) / campaignDayMs());
}

/** `budget × 1,000 / target` in whole tokens, to two decimals at most. */
function formatPerThousand(budget: bigint, target: bigint, decimals: number): string {
  const perThousand = (budget * 1_000n) / target;
  const scale = 10n ** BigInt(decimals);
  const cents = (perThousand * 100n) / scale;
  const whole = (cents / 100n).toLocaleString("en-US");
  const fraction = (cents % 100n).toString().padStart(2, "0");
  return fraction === "00" ? whole : `${whole}.${fraction}`;
}

function Section({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className={styles.card}>
      <div className="mb-5 flex gap-3">
        <span
          aria-hidden
          className="grid size-7 shrink-0 place-items-center rounded-full bg-brand-50 text-sm font-semibold text-brand-700 ring-1 ring-brand-600/20 dark:bg-brand-400/10 dark:text-brand-300 dark:ring-brand-400/20"
        >
          {number}
        </span>
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

/**
 * The brand kit: images such as the logo or the product, uploaded as they are added, each with a
 * name the visual requirements can use. At most five PNG, JPEG or WebP images of 5 MB each.
 *
 * Every change is made to the images as they are when it applies, and finds an image by its hash
 * rather than its place: an upload can end after the brand renamed or removed other images.
 */
function BrandKitField({
  images,
  onChange,
}: {
  images: BrandKitImage[];
  onChange: (update: (images: BrandKitImage[]) => BrandKitImage[]) => void;
}) {
  const upload = useUploadBrandImage();
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const hintId = useId();
  // The images as they are now, for an upload that ends later.
  const latest = useRef(images);
  useEffect(() => {
    latest.current = images;
  }, [images]);

  function add(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!(file.type in BRAND_ASSET_TYPES)) {
      setProblem("Choose a PNG, JPEG or WebP image.");
      return;
    }
    if (file.size > MAX_BRAND_ASSET_BYTES) {
      setProblem("Choose an image of at most 5 MB.");
      return;
    }
    setProblem(null);
    upload.mutate(file, {
      onSuccess: (uploaded) => {
        if (latest.current.some((image) => image.sha256 === uploaded.sha256)) {
          setProblem("That image is already in the brand kit.");
          return;
        }
        // A first name from the file's, which the brand can change.
        const name = file.name
          .replace(/\.[^.]+$/, "")
          .replace(/[-_]+/g, " ")
          .trim();
        const added: BrandKitImage = {
          label: (name || `Image ${latest.current.length + 1}`).slice(0, 40),
          sha256: uploaded.sha256,
          mediaType: uploaded.mediaType,
          preview: URL.createObjectURL(file),
        };
        onChange((current) =>
          current.some((image) => image.sha256 === added.sha256) ? current : [...current, added],
        );
      },
    });
  }

  function rename(sha256: string, label: string) {
    onChange((current) =>
      current.map((image) => (image.sha256 === sha256 ? { ...image, label } : image)),
    );
  }

  function remove(removed: BrandKitImage) {
    onChange((current) => current.filter((image) => image.sha256 !== removed.sha256));
    // An image added here is shown from memory, which it no longer needs.
    if (removed.preview.startsWith("blob:")) {
      URL.revokeObjectURL(removed.preview);
    }
  }

  return (
    <fieldset aria-describedby={hintId}>
      <legend className="text-sm font-medium">Brand kit</legend>
      <p id={hintId} className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        Up to {MAX_BRAND_ASSETS} images, such as your logo or your product, in PNG, JPEG or WebP and
        up to 5 MB each. Creators can download them, and visual requirements can name them.
      </p>
      {images.length > 0 && (
        <ul className="mt-3 grid gap-3 sm:grid-cols-2">
          {images.map((image) => (
            <li
              key={image.sha256}
              className="flex items-center gap-3 rounded-xl bg-zinc-50 p-2.5 ring-1 ring-zinc-200 dark:bg-white/[0.04] dark:ring-white/10"
            >
              {/*
                A light tile on both themes: brand kit images, such as a dark logo on a transparent
                ground, are made to be seen on white.
              */}
              <img
                src={image.preview}
                alt=""
                className="size-14 shrink-0 rounded-lg bg-white object-contain ring-1 ring-zinc-200 dark:bg-zinc-100 dark:ring-white/15"
              />
              <label className="min-w-0 flex-1 text-xs text-zinc-500 dark:text-zinc-400">
                Name
                <input
                  value={image.label}
                  maxLength={40}
                  onChange={(event) => rename(image.sha256, event.target.value)}
                  className={styles.input}
                />
              </label>
              <button
                type="button"
                aria-label={`Remove ${image.label || "the image"}`}
                onClick={() => remove(image)}
                className={styles.ghostButton}
              >
                <Remove aria-hidden className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={input}
        type="file"
        accept={Object.keys(BRAND_ASSET_TYPES).join(",")}
        onChange={add}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />
      <button
        type="button"
        disabled={images.length >= MAX_BRAND_ASSETS || upload.isPending}
        onClick={() => input.current?.click()}
        className={`mt-3 ${styles.secondaryButton}`}
      >
        <ImagePlus aria-hidden className="size-4" />
        {upload.isPending ? "Uploading…" : "Add an image"}
      </button>
      {problem && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {problem}
        </p>
      )}
      {upload.isError && <ErrorMessage error={upload.error} />}
    </fieldset>
  );
}

/** Attributes that tie a control to its label and hint. */
type FieldProps = { id: string; "aria-describedby"?: string };

/**
 * A labelled form control. The label names the control through `htmlFor`, and the hint is read
 * out with it through `aria-describedby`. Controls the app draws itself, such as a select, are
 * named through the label's id, the second argument.
 */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (props: FieldProps, labelId: string) => ReactNode;
}) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  return (
    <div>
      <label id={labelId} htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {children(hint ? { id, "aria-describedby": hintId } : { id }, labelId)}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          {hint}
        </p>
      )}
    </div>
  );
}

function ListField(props: {
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
}) {
  return (
    <Field label={props.label} hint={props.hint ?? "One per line, at most 10."}>
      {(field) => (
        <textarea
          {...field}
          rows={3}
          value={props.value}
          onChange={props.onChange}
          placeholder={props.placeholder}
          className={styles.input}
        />
      )}
    </Field>
  );
}

function Choice(props: {
  name: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex min-w-36 cursor-pointer flex-col rounded-xl px-4 py-3 text-sm ring-1 transition-colors has-focus-visible:ring-2 ${
        props.checked
          ? "bg-brand-50 ring-brand-500 dark:bg-brand-400/[0.06] dark:ring-brand-400/60"
          : "ring-zinc-300 hover:bg-zinc-50 dark:ring-white/15 dark:hover:bg-white/[0.05]"
      }`}
    >
      <span className="flex items-center gap-2 font-medium">
        <Radio name={props.name} checked={props.checked} onChange={props.onChange} />
        {props.label}
      </span>
      {props.hint && (
        <span className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{props.hint}</span>
      )}
    </label>
  );
}
